import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Download, Plus } from "lucide-react";
import { z } from "zod";
import { EmptyState } from "@/components/shared/empty-state";
import { Field, FieldGrid } from "@/components/shared/form";
import { HubToolbar } from "@/components/shared/hub-toolbar";
import { MasterFilterBar, MasterSummaryStrip } from "@/components/shared/master-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import {
  kindLabel,
  resolveLedgerSourceLink,
  splitDebitCredit,
  type LedgerSourceLink,
} from "@/lib/party-ledger";
import { cn } from "@/lib/utils";

export type PartyLedgerRow = {
  id: string;
  movement_date: string;
  kind: string;
  amount: number;
  reference: string | null;
  notes: string | null;
  source_type?: string | null;
  source_id?: string | null;
};

const adjustmentSchema = z.object({
  movementDate: z.string().min(1, "Date required"),
  amount: z.coerce.number().refine((n) => n !== 0, "Amount cannot be zero"),
  reference: z.string().optional(),
  notes: z.string().optional(),
});

type AdjustmentValues = z.infer<typeof adjustmentSchema>;

type Props = {
  partyLabel: "client" | "vendor";
  /** Used to deep-link payment rows back to the party’s payments list. */
  partyId?: string;
  balanceHint: string;
  rows: PartyLedgerRow[];
  isLoading?: boolean;
  onPostAdjustment: (values: {
    amount: number;
    movementDate: string;
    reference?: string;
    notes?: string;
  }) => Promise<unknown>;
  posting?: boolean;
};

/** Client = receivable (to receive). Vendor = payable (to pay). */
function copyFor(party: "client" | "vendor") {
  if (party === "client") {
    return {
      title: "Account statement",
      opening: "Opening / B/F",
      openingHint: "Balance before this range (to receive)",
      debit: "Invoiced",
      debitHint: "In this range only",
      credit: "Credits",
      creditHint: "Receipts & credit notes in this range",
      closing: "To receive",
      closingHint: "Opening + invoiced − credits",
      colDebit: "Invoiced",
      colCredit: "Credits",
      colBalance: "Balance (to receive)",
      closingTone: "receive" as const,
      empty:
        "Set an opening balance on the client form, or post an adjustment. Issued invoices, receipts, and credit notes post here automatically.",
      adjustHint:
        "Positive = customer owes more (to receive ↑). Negative = reduces receivable (like a receipt).",
    };
  }
  return {
    title: "Payable ledger",
    opening: "Opening / B/F",
    openingHint: "Balance before this range (to pay)",
    debit: "Billed",
    debitHint: "In this range only",
    credit: "Paid",
    creditHint: "In this range only",
    closing: "To pay",
    closingHint: "Opening + billed − paid",
    colDebit: "Billed",
    colCredit: "Paid",
    colBalance: "Balance (to pay)",
    closingTone: "pay" as const,
    empty:
      "Set an opening balance on the vendor form, or post an adjustment. Purchase bills and payments will appear here when procurement finance ships.",
    adjustHint:
      "Positive = we owe more (to pay ↑). Negative = reduces payable (like a payment).",
  };
}

export function PartyLedger({
  partyLabel,
  partyId,
  balanceHint,
  rows,
  isLoading,
  onPostAdjustment,
  posting,
}: Props) {
  const copy = copyFor(partyLabel);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);

  const form = useForm<AdjustmentValues>({
    resolver: zodResolver(adjustmentSchema),
    defaultValues: {
      movementDate: format(new Date(), "yyyy-MM-dd"),
      amount: 0,
      reference: "",
      notes: "",
    },
  });

  const allChronological = useMemo(() => {
    return rows.slice().sort((a, b) => {
      const d = a.movement_date.localeCompare(b.movement_date);
      if (d !== 0) return d;
      return a.id.localeCompare(b.id);
    });
  }, [rows]);

  const { displayLines, summary } = useMemo(() => {
    const hasFrom = Boolean(from);
    const hasTo = Boolean(to);

    /**
     * Opening in the stats strip = brought-forward into the visible period.
     * - With a From date: sum of every movement before that date (includes master OB if its as-of is earlier).
     * - Without From: the master `opening` row only (not double-counted in period totals).
     */
    const beforePeriod = hasFrom
      ? allChronological.filter((r) => r.movement_date < from)
      : [];

    const inPeriod = allChronological.filter((r) => {
      if (hasFrom && r.movement_date < from) return false;
      if (hasTo && r.movement_date > to) return false;
      return true;
    });

    const broughtForward = hasFrom
      ? beforePeriod.reduce((s, r) => s + r.amount, 0)
      : allChronological.find((r) => r.kind === "opening")?.amount ?? 0;

    const periodRows = hasFrom
      ? inPeriod
      : inPeriod.filter((r) => r.kind !== "opening");

    let periodDebit = 0;
    let periodCredit = 0;
    for (const r of periodRows) {
      const { debit, credit } = splitDebitCredit(r.amount);
      periodDebit += debit;
      periodCredit += credit;
    }

    let running = hasFrom ? broughtForward : 0;
    const chronological = inPeriod.map((r) => {
      running += r.amount;
      const { debit, credit } = splitDebitCredit(r.amount);
      return {
        ...r,
        debit,
        credit,
        balance: running,
        description: r.notes?.trim() || kindLabel(r.kind),
      };
    });

    const withBf =
      hasFrom && (broughtForward !== 0 || beforePeriod.length > 0)
        ? [
            {
              id: "__brought_forward__",
              movement_date: from,
              kind: "brought_forward",
              amount: broughtForward,
              reference: null as string | null,
              notes: null as string | null,
              debit: broughtForward > 0 ? broughtForward : 0,
              credit: broughtForward < 0 ? Math.abs(broughtForward) : 0,
              balance: broughtForward,
              description: `Brought forward as of ${format(parseISO(from), "dd MMM yyyy")}`,
            },
            ...chronological,
          ]
        : chronological;

    const closing = hasFrom
      ? broughtForward + periodRows.reduce((s, r) => s + r.amount, 0)
      : allChronological.reduce((s, r) => s + r.amount, 0);

    return {
      displayLines: withBf.slice().reverse(),
      summary: {
        opening: broughtForward,
        periodDebit,
        periodCredit,
        closing,
      },
    };
  }, [allChronological, from, to]);

  function exportCsv() {
    const header = [
      "date",
      "description",
      "reference",
      "kind",
      copy.colDebit.toLowerCase(),
      copy.colCredit.toLowerCase(),
      "balance",
    ];
    const body = displayLines.map((l) =>
      [
        l.movement_date,
        csv(l.description),
        csv(l.reference ?? ""),
        l.kind,
        l.debit || "",
        l.credit || "",
        l.balance,
      ].join(","),
    );
    const blob = new Blob([[header.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${partyLabel}-ledger-${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    await onPostAdjustment({
      amount: values.amount,
      movementDate: values.movementDate,
      reference: values.reference?.trim() || "Adjustment",
      notes: values.notes?.trim() || undefined,
    });
    setSheetOpen(false);
    form.reset({
      movementDate: format(new Date(), "yyyy-MM-dd"),
      amount: 0,
      reference: "",
      notes: "",
    });
  });

  const errors = form.formState.errors;

  return (
    <div>
      <HubToolbar
        title={copy.title}
        description={balanceHint}
        action={
          <div className="flex flex-wrap items-center gap-1.5">
            <Button type="button" size="sm" onClick={() => setSheetOpen(true)}>
              <Plus />
              Adjustment
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!displayLines.length}
            >
              <Download />
              Export
            </Button>
          </div>
        }
      />

      <div className="space-y-2 border-b border-border px-3 py-2">
        <MasterFilterBar>
          <div className="space-y-0.5">
            <label className="field-label" htmlFor="ledger-from">
              From
            </label>
            <Input
              id="ledger-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-32"
            />
          </div>
          <div className="space-y-0.5">
            <label className="field-label" htmlFor="ledger-to">
              To
            </label>
            <Input
              id="ledger-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-32"
            />
          </div>
          {(from || to) && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="self-end"
              onClick={() => {
                setFrom("");
                setTo("");
              }}
            >
              Clear
            </Button>
          )}
        </MasterFilterBar>

        <MasterSummaryStrip
          items={[
            {
              label: copy.opening,
              value: formatMoney(summary.opening),
            },
            {
              label: copy.debit,
              value: formatMoney(summary.periodDebit),
            },
            {
              label: copy.credit,
              value: formatMoney(summary.periodCredit),
              tone: "success",
            },
            {
              label: copy.closing,
              value: formatMoney(summary.closing),
              tone: copy.closingTone === "pay" ? "warning" : "default",
            },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-xs text-muted-foreground">Loading…</p>
      ) : displayLines.length === 0 ? (
        <div className="p-6">
          <EmptyState
            title="No ledger entries yet"
            description={copy.empty}
            action={
              <Button type="button" size="sm" onClick={() => setSheetOpen(true)}>
                <Plus />
                Add adjustment
              </Button>
            }
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right text-primary">{copy.colDebit}</TableHead>
              <TableHead className="text-right text-success">{copy.colCredit}</TableHead>
              <TableHead
                className={cn(
                  "text-right",
                  copy.closingTone === "receive" ? "text-primary" : "text-warning",
                )}
              >
                {copy.colBalance}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayLines.map((line) => (
              <TableRow key={line.id}>
                <TableCell className="whitespace-nowrap tabular-nums">
                  {format(parseISO(line.movement_date), "dd MMM yyyy")}
                </TableCell>
                <TableCell>
                  <div className="text-xs font-medium">{line.description}</div>
                  <div className="text-2xs text-muted-foreground">
                    {line.kind === "brought_forward" ? "Brought forward" : kindLabel(line.kind)}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-2xs text-muted-foreground">
                  <LedgerReference
                    reference={line.reference}
                    source={resolveLedgerSourceLink({
                      sourceType: line.source_type,
                      sourceId: line.source_id,
                      partyId,
                    })}
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums text-primary">
                  {line.debit ? formatMoney(line.debit) : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums text-success">
                  {line.credit ? formatMoney(line.credit) : "—"}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-medium tabular-nums",
                    copy.closingTone === "receive" ? "text-primary" : "text-warning",
                  )}
                >
                  {formatMoney(line.balance)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Post adjustment</SheetTitle>
            <SheetDescription>{copy.adjustHint}</SheetDescription>
          </SheetHeader>
          <form id="ledger-adjustment" onSubmit={onSubmit} className="flex-1 overflow-y-auto p-4">
            <FieldGrid>
              <Field label="Date" required span={6} error={errors.movementDate?.message}>
                <Input type="date" {...form.register("movementDate")} />
              </Field>
              <Field label="Amount" required span={6} error={errors.amount?.message}>
                <Input type="number" step="0.01" {...form.register("amount")} />
              </Field>
              <Field label="Reference" span={12}>
                <Input {...form.register("reference")} placeholder="ADJ-001" />
              </Field>
              <Field label="Notes" span={12}>
                <Textarea rows={2} {...form.register("notes")} />
              </Field>
            </FieldGrid>
          </form>
          <SheetFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="ledger-adjustment" size="sm" disabled={posting}>
              Post
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LedgerReference({
  reference,
  source,
}: {
  reference: string | null;
  source: LedgerSourceLink;
}) {
  const label = reference?.trim() || "—";
  if (!source) return <>{label}</>;

  const className = "text-primary underline-offset-2 hover:underline";

  if (source.kind === "invoice") {
    return (
      <Link to="/invoices/$invId" params={{ invId: source.id }} className={className}>
        {label === "—" ? "Invoice" : label}
      </Link>
    );
  }
  if (source.kind === "credit_note") {
    return (
      <Link to="/credit-notes/$cnId" params={{ cnId: source.id }} className={className}>
        {label === "—" ? "Credit note" : label}
      </Link>
    );
  }
  return (
    <Link to="/payments" search={{ client: source.clientId }} className={className}>
      {label === "—" ? "Payment" : label}
    </Link>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
